// @ts-nocheck
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setModalOpen, modalViewsConst, setModalState } from "../../store/modalSlice";
import { ReactComponent as GroupIcon } from "../../assets/icons/group-icon.svg";
import {
  postAssetGroup,
  editAssetGroupFunc,
  cascadeAssetGroupOrganization,
} from "../../store/assetGroupSlice";
import { getMyOrganizations } from "../../store/userSlice";
import { z } from "zod";
import { useForm, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { getOwnerFromId } from "../../util/utils";
import { v4 as uuidv4 } from "uuid";
import ModalBase from "./ModalBase";
import ProfilePictureRounded from "../ProfilePictureRounded";
import { getUrl } from "@aws-amplify/storage";
import { ImageUploadTrigger } from "../imageUploader/ImageUploadFlow";

import Select, { Field } from "../ReusableComponents/Select";
const AssetGroupNameSchema = z.object({
  name: z.string().trim().min(2).max(50),
  organizationId: z.string().optional(),
});
type AssetGroupNameType = z.infer<typeof AssetGroupNameSchema>;

interface NewAssetGroupModalProps {
  editFlag?: boolean;
  initialAssetGroup?: any;
  onClose?: (result?: any) => void;
}

// Helper function to get the organization owner's name
const getOrganizationOwnerName = (organization) => {
  if (!organization || !organization.owner) return "Not specified";
  
  // Look for the owner in the members array
  const ownerId = organization.owner;
  const ownerMember = organization.members?.find(member => member.id === ownerId);
  
  if(ownerMember?.profilePictureUrl && ownerMember?.name) {
    return (
      <div className="flex my-2">
        <ProfilePictureRounded
                    pictureURL={ownerMember?.profilePictureUrl} 
                    diameter={32}
                    className="cursor-pointer"
                  />
        <div className="p-2">{ownerMember?.name}</div>
      </div>
    )
  }
  if (ownerMember?.name) {
    return `${ownerMember.name}`.trim();
  }
  
  // Fallback to owner ID if no member info is found
  return ownerId;
};

const NewAssetGroupModal: React.FC<NewAssetGroupModalProps> = ({ 
  editFlag = false, 
  initialAssetGroup,
  onClose
}) => {
  const [formStep, setFormStep] = useState(1);
  const [owner, setOwner] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [imageFilePath, setImageFilePath] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { organizations, details,  permissionDetails } = useSelector((state) => state.user);
  const modalState = useSelector((state) => state.modalState);
  const hasOrganizations = organizations.loaded && Object.keys(organizations.data).length > 0;
  const groups = details?.groups ?? []
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<AssetGroupNameType>({
    resolver: zodResolver(AssetGroupNameSchema),
    mode: "onChange",
  });
  
  const selectedOrganizationId = watch("organizationId");
  const onSubmit: SubmitHandler<AssetGroupNameType> = async (data) => {
    try {
      let result;
      let savedGroup;
      const isAdmin = groups?.includes("Admin");
      
      if (editFlag) {
        let updatedAssetGroup = { id: initialAssetGroup.id, ...data };
        if (imageFilePath) {
          updatedAssetGroup["profilePictureUrl"] = imageFilePath;
        }
        
        // Handle owner assignment based on organization selection and admin status
        if (data.organizationId && organizations.data[data.organizationId]) {
          // For admins or organization owners, we can set the organization
          const isOrgOwner = organizations.data[data.organizationId].owner === permissionDetails?.id;
          
          if (isAdmin || isOrgOwner) {
            // If organization is changed by admin, update the owner to the org owner
            if (data.organizationId !== initialAssetGroup.organizationId) {
              updatedAssetGroup["owner"] = organizations.data[data.organizationId].owner;
            } else if (owner) {
              // Otherwise keep the existing owner if possible
              updatedAssetGroup["owner"] = owner.id;
            }
          }
        } else if (owner) {
          // No organization, use manually selected owner or keep existing one
          updatedAssetGroup["owner"] = owner.id;
        }
        
        result = await dispatch(editAssetGroupFunc(updatedAssetGroup));
        savedGroup = {...updatedAssetGroup, ...result.payload};

        // The group's org must reach its assets and their links too, or the org's members can't see them. Run on
        // every Admin save with an org (not only on a change), so a partly failed earlier cascade is retried; it
        // writes only rows that differ. Admin-only: a non-admin owner can't update Admin-created assets.
        const orgChanged =
          data.organizationId !== undefined &&
          (data.organizationId || null) !== (initialAssetGroup.organizationId || null);
        if (!result.error && isAdmin && (orgChanged || data.organizationId)) {
          const cascade = await dispatch(
            cascadeAssetGroupOrganization({
              assetGroupId: initialAssetGroup.id,
              organizationId: data.organizationId || null,
            })
          );
          if (cascade.error) throw new Error(String(cascade.payload));
        }
      } else {
        const newID = uuidv4();
        let newAssetGroup = { id: newID, ...data };
        
        // Set owner based on organization, admin status, or manually selected owner
        if (data.organizationId && organizations.data[data.organizationId]) {
          // For admins or organization owners, we can set the organization
          const isOrgOwner = organizations.data[data.organizationId].owner === permissionDetails?.id;
          
          if (isAdmin || isOrgOwner) {
            // Use the organization's owner
            newAssetGroup["owner"] = organizations.data[data.organizationId].owner;
          }
        } else if (owner) {
          // Use manually selected owner if no organization
          newAssetGroup["owner"] = owner.id;
        }
        
        result = await dispatch(postAssetGroup(newAssetGroup));
        savedGroup = {...newAssetGroup, ...result.payload};
      }

      // Thunks resolve on failure (rejectWithValue), so surface it to the catch block
      if (result?.meta?.requestStatus === "rejected") {
        throw new Error(result.payload || "Failed to save asset group");
      }

      // Use callback if provided
      if (onClose) {
        onClose({
          success: true,
          action: editFlag ? 'update' : 'create',
          assetGroup: savedGroup
        });
      } else {
        // Backward compatibility
        dispatch(setModalOpen(false));
      }

      // After creating a new group, take the user to its page
      if (!editFlag && savedGroup?.id) {
        navigate(`/home/group/${savedGroup.id}`);
      }
    } catch (error) {
      console.error("Error saving asset group:", error);

      if (onClose) {
        onClose({
          success: false,
          error,
          action: editFlag ? 'update' : 'create'
        });
      } else {
        dispatch(setModalOpen(false));
      }

      // Thunk failures used to close silently; tell the user it didn't save
      dispatch(
        setModalState({
          modalOpen: true,
          modalView: modalViewsConst.notification,
          properties: {
            __modalSize: "sm",
            header: "Something went wrong",
            details: `We couldn't ${editFlag ? "update" : "create"} the asset group. Please try again.`,
          },
        })
      );
    }
  };

  const getOwner = async () => {
    const assetGroupOwner = await getOwnerFromId(initialAssetGroup.owner)
    console.log("owner", assetGroupOwner)
    setOwner(assetGroupOwner)
    
  }

  // Function to fetch existing profile image
  const getProfileImage = async () => {
    if (initialAssetGroup?.profilePictureUrl) {
      try {
        const { url } = await getUrl({
          path: initialAssetGroup.profilePictureUrl,
          options: {
            validateObjectExistence: true,
            expiresIn: 3600
          }
        });
        setImagePreviewUrl(url);
      } catch (error) {
        console.error("Error fetching profile image:", error);
      }
    }
  };

  useEffect(() => {
    if (editFlag) {
      setValue("name", initialAssetGroup.name);
      if (initialAssetGroup.organizationId) {
        setValue("organizationId", initialAssetGroup.organizationId);
      }
      
      // Ensure organizations data is loaded if admin or viewing an organization-owned asset group
      if (!organizations.loaded && (groups?.includes("Admin") || initialAssetGroup.organizationId)) {
        dispatch(getMyOrganizations());
      }
      
      getOwner();
      getProfileImage();
    } else {
      // For new asset groups, ensure organizations are loaded if user is an admin
      if (!organizations.loaded && groups?.includes("Admin")) {
        dispatch(getMyOrganizations());
      }
    }
  }, []);

  useEffect(() => {
    if (!editFlag) return;
    if (
      modalState?.lastResult?.action === "updateImage" &&
      modalState?.lastResult?.uploadTarget === "assetGroup" &&
      modalState?.lastResult?.assetGroupId === initialAssetGroup?.id &&
      modalState?.lastResult?.imageUrl
    ) {
      setImageFilePath(modalState.lastResult.imageUrl);
      const loadImagePreview = async () => {
        try {
          const { url } = await getUrl({
            path: modalState.lastResult.imageUrl,
            options: {
              validateObjectExistence: true,
              expiresIn: 3600,
            },
          });
          setImagePreviewUrl(url.toString());
        } catch (error) {
          console.error("Error loading group image preview:", error);
        }
      };
      loadImagePreview();
    }
  }, [modalState?.lastResult]);

  const handleOpenImageModal = () => {
    if (!editFlag || !initialAssetGroup?.id) return;
    
    const groupForModal = {
      id: initialAssetGroup.id,
      name: initialAssetGroup?.name || watch("name") || "Asset Group",
      imageUrl: imageFilePath || initialAssetGroup?.profilePictureUrl || null,
      organizationId: initialAssetGroup?.organizationId || watch("organizationId") || null,
      profilePictureUrl: initialAssetGroup?.profilePictureUrl || null,
    };

    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.assetGroupImageUpload,
        properties: groupForModal,
      })
    );
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    } else {
      dispatch(setModalOpen(false));
    }
  };

  // Hands off to the shared confirm modal, so deleting warns before anything is removed
  const handleDeleteGroup = () => {
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.deleteAssetGroup,
        properties: { ...initialAssetGroup, __modalSize: "sm" },
      })
    );
  };

  const isAdmin = groups?.includes("Admin");
  const orgOptions = Object.entries(organizations.data);

  return (
    <ModalBase
      title={`${editFlag ? "Edit Existing" : "New"} Asset Group`}
      icon={<GroupIcon className="h-6 w-6" />}
      onClose={handleCancel}
      size="sm"
      footer={
        <>
          {/* mr-auto: the footer is justify-end, and the destructive action belongs alone at the leading edge */}
          {editFlag && initialAssetGroup?.id && (!initialAssetGroup?.organizationId || isAdmin) && (
            <button className="eai-btn-danger mr-auto" type="button" onClick={handleDeleteGroup}>
              Delete Group
            </button>
          )}
          <button className="eai-btn-outline" type="button" onClick={handleCancel}>
            Cancel
          </button>
          <button className="eai-btn-cta" type="submit" form="asset-group-name-form">
            {editFlag ? "Update" : "Create"}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} id="asset-group-name-form" className="flex flex-col gap-5">
        <Field label="Group name" htmlFor="asset-group-name" error={errors.name?.message}>
          <input
            {...register("name")}
            id="asset-group-name"
            data-testid="asset-group-name-input"
            placeholder="e.g. Columbia River Mainstem"
            className="eai-input"
          />
        </Field>

        {/* Organization picker — new groups */}
        {hasOrganizations && !editFlag && (
          <Select
            {...register("organizationId")}
            label="Organization (optional)"
            hint={
              isAdmin
                ? "As an admin, you can assign groups to any organization."
                : "You can only assign groups to organizations you own."
            }
            containerClassName="w-full"
          >
            <option value="">No Organization</option>
            {orgOptions.map(([id, org]) => {
              const canSelectOrg = isAdmin || org.owner === permissionDetails?.id;
              return (
                <option key={id} value={id} disabled={!canSelectOrg}>
                  {org.name}
                  {!canSelectOrg && !isAdmin ? " (Not owned by you)" : ""}
                  {isAdmin && org.owner !== permissionDetails?.id ? " (Admin access)" : ""}
                </option>
              );
            })}
          </Select>
        )}
        {hasOrganizations && !editFlag && selectedOrganizationId && (
          <p className="-mt-3 text-eai-body-xs text-eai-warning-text">
            All assets created in this group will be part of the organization. This cannot be undone yet.
          </p>
        )}

        {/* Organization — existing groups */}
        {editFlag && isAdmin && (
          <Select
            {...register("organizationId")}
            defaultValue={initialAssetGroup?.organizationId || ""}
            label="Organization"
            hint="As an admin, you can reassign this group to any organization."
            containerClassName="w-full"
          >
            <option value="">No Organization</option>
            {orgOptions.map(([id, org]) => (
              <option key={id} value={id}>
                {org.name}
                {id === initialAssetGroup?.organizationId ? " (Current)" : ""}
                {org.owner !== permissionDetails?.id ? " (Admin access)" : ""}
              </option>
            ))}
          </Select>
        )}
        {editFlag && isAdmin && selectedOrganizationId && (
          <p className="-mt-3 text-eai-body-xs text-eai-warning-text">
            Changing organization assignment will affect all assets in this group.
          </p>
        )}

        {editFlag && !isAdmin && (
          <Field
            label="Organization"
            hint={
              initialAssetGroup?.organizationId && organizations?.data[initialAssetGroup.organizationId]
                ? `Organization Owner: ${getOrganizationOwnerName(organizations.data[initialAssetGroup.organizationId])}`
                : undefined
            }
          >
            <div className="eai-input-readonly">
              {initialAssetGroup?.organizationId && organizations?.data[initialAssetGroup.organizationId]
                ? organizations.data[initialAssetGroup.organizationId].name
                : "No Organization"}
            </div>
          </Field>
        )}

        {editFlag && (
          <ImageUploadTrigger
            label="Group Image (Optional)"
            previewUrl={imagePreviewUrl}
            onClick={handleOpenImageModal}
          />
        )}
      </form>
    </ModalBase>
  );
};

export default NewAssetGroupModal;
